import React from 'react';
import logo from "../img/COBRADOR_LEAGUE_LOGO_02_FINAL.png";
import { usePlayerLeagues } from "../hooks/usePlayerLeagues";
import { usePlayerMatches } from "../hooks/usePlayerMatches";
import { usePlayerArmyLeaderboard } from "../hooks/usePlayerArmyLeaderboard";
import { usePlayerHonor } from "../hooks/usePlayerHonor";
import { getArmyIcon } from "../util/getArmyIcon";

const PlayerPopup = ({ player, league, onClose }) => {
  const { leagues, loading: loadingLeagues } = usePlayerLeagues(player?.idplayer);
  const { matches, loading: loadingMatches } = usePlayerMatches(player?.idplayer, league);
  const { leaderboard, loading: loadingLeaderboard } = usePlayerArmyLeaderboard(player?.idplayer, league);
  const { playerHonorData, loading: loadingHonor } = usePlayerHonor(player?.idplayer, league);

  if (!player) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 text-white p-8 rounded shadow-lg w-[1500px] flex flex-col gap-6">

        <div className="flex gap-8">

          {/* BLOQUE IZQUIERDO */}
          <div className="w-[500px] rounded-lg bg-gray-800 p-6 shadow-lg">
            <h2 className="text-3xl font-bold mb-6 text-center">Player Info</h2>

            <div className="flex items-center space-x-5 mb-6">
              <img src={logo} alt="Logo" className="w-20 h-20 rounded-full" />
              <div>
                <p className="text-xl"><strong>{player.player || "Not available"}</strong></p>
                <p className="text-xl"><strong>"{player.nick}"</strong></p>
              </div>
            </div>

            <h3 className="text-2xl font-semibold mb-3">Leagues History:</h3>
            {loadingLeagues ? (
              <p className="text-gray-400 text-xl">Loading leagues...</p>
            ) : Array.isArray(leagues) && leagues.length > 0 ? (
              <table className="w-full text-xl bg-gray-700 rounded mb-6">
                <thead>
                  <tr className="bg-gray-600">
                    <th className="px-3 py-2 text-left">League</th>
                    <th className="px-3 py-2 text-center">Position</th>
                  </tr>
                </thead>
                <tbody>
                  {leagues.map((leagueItem, index) => (
                    <tr key={index} className="hover:bg-gray-600">
                      <td className="px-3 py-2">{leagueItem.league_name}</td>
                      <td className="px-3 py-2 text-center">{leagueItem.position ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-400 text-xl">The player has not participated in any league.</p>
            )}

            <h3 className="text-2xl font-semibold mb-3">Army Performance in Active League:</h3>
            {loadingLeaderboard ? (
              <p className="text-gray-400 text-xl">Loading army stats...</p>
            ) : Array.isArray(leaderboard) && leaderboard.length > 0 ? (
              <table className="w-full text-xl bg-gray-700 rounded">
                <thead>
                  <tr className="bg-gray-600 text-center">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Army</th>
                    <th className="px-3 py-2">P</th>
                    <th className="px-3 py-2">W</th>
                    <th className="px-3 py-2">L</th>
                    <th className="px-3 py-2">D</th>
                    <th className="px-3 py-2">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((army, index) => (
                    <tr key={index} className="hover:bg-gray-600 text-center">
                      <td className="px-3 py-2">{army.position}</td>
                      <td className="px-3 py-2">{army.army_name}</td>
                      <td className="px-3 py-2">{army.played}</td>
                      <td className="px-3 py-2">{army.won}</td>
                      <td className="px-3 py-2">{army.lost}</td>
                      <td className="px-3 py-2">{army.drawn}</td>
                      <td className="px-3 py-2">{army.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-400 text-xl">No army performance data available.</p>
            )}
          </div>

          {/* BLOQUE CENTRAL — LATEST MATCHES (COMPACTADO) */}
          <div className="flex-1 rounded-lg bg-gray-800 p-6 shadow-lg">
            <h2 className="mb-4 text-2xl font-semibold text-center">Latest Matches</h2>

            <div className="space-y-3 max-h-[680px] overflow-y-auto">
              {loadingMatches ? (
                <p className="text-gray-400 text-center text-lg">Loading matches...</p>
              ) : Array.isArray(matches) && matches.length > 0 ? (
                matches.slice(0, 6).map((match) => {
                  const ArmyIconP1 = getArmyIcon(match.army_p1);
                  const ArmyIconP2 = getArmyIcon(match.army_p2);

                  return (
                    <div key={match.id} className="rounded-lg bg-gray-700 p-3 space-y-2">

                      <div className="text-gray-400 text-center text-base">
                        {match.division_name} [{match.date}]
                      </div>

                      <div className="grid grid-cols-[1fr_1fr_0.5fr_0.5fr_0.5fr_1fr_1fr] items-center text-center gap-x-1">

                        <div>
                          {ArmyIconP1 && (
                            <img src={ArmyIconP1} alt="Army 1 Icon" className="w-9 h-9 mx-auto" />
                          )}
                        </div>

                        <div className="text-right text-white text-lg">
                          {match.nick1}
                          <br />
                          <p className="text-gray-300 text-base">{match.army_p1}</p>
                        </div>

                        <div className={(match.score_p1 > match.score_p2 ? "text-green-300" : match.score_p1 < match.score_p2 ? "text-red-300" : "") + " text-2xl font-bold text-right"}>
                          {match.score_p1}
                        </div>

                        <div className="text-white col-span-1 flex justify-center text-lg">vs</div>

                        <div className={(match.score_p2 > match.score_p1 ? "text-green-300" : match.score_p2 < match.score_p1 ? "text-red-300" : "") + " text-2xl font-bold text-left"}>
                          {match.score_p2}
                        </div>

                        <div className="text-left text-white text-lg">
                          {match.nick2}
                          <br />
                          <p className="text-gray-300 text-base">{match.army_p2}</p>
                        </div>

                        <div>
                          {ArmyIconP2 && (
                            <img src={ArmyIconP2} alt="Army 2 Icon" className="w-9 h-9 mx-auto" />
                          )}
                        </div>

                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-400 text-center text-lg">There are no matches registered.</p>
              )}
            </div>
          </div>

          {/* BLOQUE DERECHO — HONORS */}
          <div className="w-[250px] rounded-lg bg-gray-800 p-6 shadow-lg border-l border-gray-700">
            <h2 className="mb-6 text-2xl font-semibold text-center">Honors</h2>

            {loadingHonor ? (
              <p className="text-gray-400 text-center">Loading honors...</p>
            ) : Array.isArray(playerHonorData) && playerHonorData.length > 0 ? (
              <div className="grid grid-cols-1 gap-6">
                {playerHonorData.map((honor, index) => (
                  <div key={index} className="flex flex-col items-center relative group">
                    <img
                      src={honor.picture}
                      alt={honor.name}
                      className="w-40 h-40 object-contain rounded cursor-pointer"
                    />
                    <p className="text-center text-lg mt-3 font-medium">{honor.name}</p>

                    <div className="absolute bottom-full mb-3 hidden group-hover:block w-max max-w-xs bg-gray-900 text-white rounded px-3 py-2 text-center z-50">
                      <p className="font-bold text-xl">{honor.name}</p>
                      <p className="text-base mt-1">{honor.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center">This player has no honors yet.</p>
            )}
          </div>

        </div>

        {/* BOTÓN CERRAR */}
        <div className="flex justify-center mt-4">
          <button
            className="px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 text-xl"
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>

      </div>
    </div>
  );
};

export default PlayerPopup;

